import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import type { AppRegistryPage } from '@plato/app-meta/ai-meta';
import { usePageAiMeta } from '@plato/app-shared/hooks/use-page-ai-meta';
import type { CrmDisplayConfig } from '@plato/data/model/crm-config';
import { SrCustomerAddressType } from '@plato/data/starrocks/types';
import { BuildingIcon } from '@plato/ui-legacy/assets/icons/building-icon';
import { PersonIcon } from '@plato/ui-legacy/assets/icons/person-icon';
import { FullPage404 } from '@plato/ui-legacy/components/common/full-page-404';
import { LoadingBlockFullPage } from '@plato/ui-legacy/components/loading/loading-block-full-page';
import { TabContentTableWrapper } from '@plato/ui-legacy/components/tables/tab-content-table-wrapper';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { Page } from '@plato/ui-legacy/components/ui/page';
import { Tabs, TabsItem } from '@plato/ui-legacy/components/ui/tabs';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { DataTable } from '@plato/ui/components/data-table';
import { Pencil } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { GoogleMapsLink } from '../../../components/common/google-maps-link.tsx';
import { useAnalytics } from '../../../features/analytics/hooks/use-analytics.ts';
import {
    CrmContextProvider,
    useDraftData,
    type CrmContext,
} from '../../../features/crm/components/crm-context.tsx';
import { CrmTab, getTabKey } from '../../../features/crm/components/crm-tab.tsx';
import { useCrmConfig } from '../../../features/crm/hooks/use-crm-config.ts';
import { CustomerDetailCreditLimit } from '../../../features/customer-detail/components/customer-detail-credit-limit.tsx';
import { CustomerDetailHeader } from '../../../features/customer-detail/components/customer-detail-header.tsx';
import {
    CustomerDetailNavigation,
    CustomerDetailPathOverrideEnum,
} from '../../../features/customer-detail/components/customer-detail-navigation.tsx';
import { useCustomerAddressesColumns } from '../../../features/list-views/columns/use-customer-addresses-columns.tsx';
import { useCustomerContactsColumns } from '../../../features/list-views/columns/use-customer-contacts-columns.tsx';
import { getFullAddressString } from '../../../lib/address-helper.ts';
import type { CustomerDTO } from '../../../server/dto/customer-dto.ts';
import { api } from '../../../utils/api.ts';

export function useCustomerInfoAiMeta(): AppRegistryPage {
    const { t } = useTranslation();
    return usePageAiMeta({
        path: '/customers/:customer_id/info',
        title: t('navigation_tab__info', { ns: 'customer-detail' }),
        description:
            "The customer's data tab: address, industry, assigned sales reps, the credit limit " +
            'where the tenant allows it, tenant-configured fields that may be editable, the ' +
            'contacts table and the ship-to addresses table. Send the user here to look up or ' +
            'change who and where a customer is, not for figures.',
    });
}

function CustomerDetail() {
    const params = useParams<{ customer_id: string }>();
    const { toast } = useToast();
    const apiUtils = api.useUtils();
    const sendEvent = useAnalytics();
    const { canReadSalesRepAssignment, canReadCustomerContacts, canReadShipToAddresses } = useAppConfig();
    const { data: crmConfig, isLoading: crmConfigLoading } = useCrmConfig();
    const { crmCustomFieldsConfig, crmDisplayConfig } = crmConfig ?? {};
    const [editAll, setEditAll] = useState(false);

    const [selectedTab, setSelectedTab] = useState<string>('contacts');
    const erpCustomerId = params.customer_id || '';

    const { t } = useTranslation('customer-detail');

    const getCustomerTabLabel = (labelKey: string): string | undefined => {
        switch (labelKey) {
            case 'accounting':
                return t('tab__customer__accounting');
            case 'general':
                return t('tab__customer__general');
            case 'sales':
                return t('tab__customer__sales');
            default:
                return undefined;
        }
    };

    const { data: apiData, isLoading } = api.platoUser.getDetailCustomer.getCrmDetail.useQuery({
        erpCustomerId,
    });

    useEffect(() => {
        if (erpCustomerId) {
            sendEvent('customer_detail_info_viewed', {
                customer_id: erpCustomerId,
            });
        }
    }, [erpCustomerId, sendEvent]);

    const { mutate: saveCustomer, isPending: isSaving } = api.platoUser.command.customers.update.useMutation({
        onError: async () => {
            toast({ icon: 'warning', title: t('save_error') });
            reset();
        },

        onSuccess: async () => {
            toast({ icon: 'success', title: t('save_successful') });
            setEditAll(false);

            void apiUtils.platoUser.getDetailCustomer.getCrmDetail.invalidate({
                erpCustomerId,
            });

            sendEvent('customer_fields_edited', {
                customer_id: erpCustomerId,
            });
        },
    });

    const {
        data: draftData,
        reset,
        save,
        setData,
    } = useDraftData<CustomerDTO>({
        data: apiData,
        disableAutoSave: editAll,
        save: saveCustomer,
    });

    function abandon() {
        reset();
        setEditAll(false);
    }

    const context = useMemo(
        (): CrmContext<'customer'> => ({
            data: draftData,
            entityType: 'customer',
            customFieldConfig: crmCustomFieldsConfig,
            setData,
        }),
        // eslint-disable-next-line react-hooks/preserve-manual-memoization -- legacy code
        [draftData, crmCustomFieldsConfig, setData],
    );

    const selectedCrmTabConfig = crmDisplayConfig?.customer?.tabs.find(
        (tab) => getTabKey(tab) === selectedTab,
    );

    const noFieldsEditable = !selectedCrmTabConfig?.fields.some((field) => field.writable);

    if (isLoading) {
        return <LoadingBlockFullPage className=" mt-32" />;
    }

    if (!apiData) {
        return <FullPage404 />;
    }

    return (
        <Page>
            <CustomerDetailHeader customer={apiData} />

            <CustomerDetailNavigation
                pathOverride={CustomerDetailPathOverrideEnum.INFO}
                customerId={erpCustomerId}
            />

            {!crmDisplayConfig && !crmConfigLoading && (
                <div className="flex gap-3 flex-col sm:flex-row">
                    <div className="flex flex-3 flex-col rounded-md bg-neutral-50 p-3">
                        <div className="text-sm font-normal leading-6 text-neutral-900 xl:text-base">
                            {apiData.customerId} <span className="font-medium">{apiData.customerName}</span>
                        </div>
                        <div className="flex items-center gap-1 text-sm font-normal text-neutral-500">
                            <span>{getFullAddressString(apiData.address, apiData.customerName)}</span>
                            <GoogleMapsLink address={apiData.address} />
                        </div>
                    </div>
                    <div className="flex flex-2 flex-col items-start justify-start gap-3 rounded-md bg-neutral-50 p-3 text-sm font-normal text-neutral-700">
                        <div className="flex items-center space-x-2">
                            <BuildingIcon w-4 className="flex shrink-0" />{' '}
                            <span>{apiData.industrySector || '-'}</span>
                        </div>
                        {canReadSalesRepAssignment && (
                            <div className="flex items-center space-x-2">
                                <PersonIcon className=" flex w-4 shrink-0" />{' '}
                                <span>
                                    {apiData.salesReps.length > 0
                                        ? apiData.salesReps
                                              .map((rep) => `${rep.salesRepId}: ${rep.salesRepName}`)
                                              .join(', ')
                                        : '-'}
                                </span>
                            </div>
                        )}
                    </div>
                    <CustomerDetailCreditLimit customerId={erpCustomerId} />
                </div>
            )}

            <CrmContextProvider value={context}>
                <Tabs
                    disabled={editAll}
                    selectedTab={selectedTab}
                    onTabSelected={setSelectedTab}
                    toolbox={
                        selectedCrmTabConfig && (
                            <div className="flex gap-2">
                                {editAll ? (
                                    <>
                                        <Button onClick={abandon} variant="subtle">
                                            {t('action__cancel_edit_all')}
                                        </Button>
                                        <Button onClick={save}>{t('action__save_edit_all')}</Button>
                                    </>
                                ) : (
                                    <Button
                                        disabled={isSaving || noFieldsEditable}
                                        onClick={() => {
                                            setEditAll(true);
                                        }}
                                        icon={Pencil}
                                        variant="subtle"
                                    >
                                        {t('action__edit_all')}
                                    </Button>
                                )}
                            </div>
                        )
                    }
                >
                    {canReadCustomerContacts && (!crmDisplayConfig || !!crmDisplayConfig.contact) && (
                        <TabsItem tabId="contacts" label={t('tab_title__contacts')} className="flex flex-col">
                            <ContactsTab config={crmDisplayConfig} erpCustomerId={erpCustomerId} />
                        </TabsItem>
                    )}
                    {canReadShipToAddresses && (!crmDisplayConfig || !!crmDisplayConfig.address) && (
                        <TabsItem
                            tabId="addresses"
                            label={t('tab_title__addresses')}
                            className="flex flex-col"
                        >
                            <AddressesTab config={crmDisplayConfig} erpCustomerId={erpCustomerId} />
                        </TabsItem>
                    )}
                    {crmDisplayConfig?.customer?.tabs.map((tab) => {
                        let label: string | undefined;
                        if (tab.label) {
                            label = tab.label;
                        } else if (tab.labelKey) {
                            label = getCustomerTabLabel(tab.labelKey);
                        }

                        return (
                            <TabsItem tabId={getTabKey(tab)} key={getTabKey(tab)} label={label ?? ''}>
                                <CrmTab
                                    config={tab}
                                    data={draftData}
                                    disabled={isSaving || isLoading}
                                    mode={editAll ? 'edit' : 'auto'}
                                    onSave={save}
                                    onAbandon={abandon}
                                />
                            </TabsItem>
                        );
                    })}
                </Tabs>
            </CrmContextProvider>
        </Page>
    );
}

type ContactAddressTabProps = {
    config?: CrmDisplayConfig;
    erpCustomerId: string;
};

function ContactsTab({ config, erpCustomerId }: ContactAddressTabProps) {
    const { t } = useTranslation();
    const { data, isLoading } = api.platoUser.getManyCustomerContact.get.useQuery({
        customerId: erpCustomerId,
    });

    const contactsColumns = useCustomerContactsColumns({ config: config?.contact });

    return (
        <DataTable
            emptyLabel={t('empty_table__no_results')}
            isLoading={isLoading}
            data={data ?? []}
            columns={contactsColumns}
        />
    );
}

function AddressesTab({ config, erpCustomerId }: ContactAddressTabProps) {
    const { t } = useTranslation();
    const { data, isLoading } = api.platoUser.query.customerAddress.listDetail.useQuery({
        customerId: erpCustomerId,
        types: [SrCustomerAddressType.SHIPPING],
    });

    const addressesColumns = useCustomerAddressesColumns({ config: config?.address });

    return (
        <TabContentTableWrapper>
            <DataTable
                emptyLabel={t('empty_table__no_results')}
                isLoading={isLoading}
                data={data ?? []}
                columns={addressesColumns}
            />
        </TabContentTableWrapper>
    );
}

export default CustomerDetail;
